import React, { useMemo, useState } from "react";

import {
  MessageBar,
  MessageBarType,
  NoDataTile,
  Spinner,
  Stack,
  Text,
  useTheme
} from "@bps/fluent-ui";
import { withPermissions } from "@components/withPermissions";
import { BpIdUsersSearchArgs } from "@libs/api/gateways/bp-id/bp-id.dtos";
import { Permissions } from "@libs/permissions/permissions.enum";
import {
  useConsumerUserProfileSearch,
  useCUIdentitySearch
} from "@libs/api/gateways/best-health/account/best-health-account-ops-gateway.hooks";
import { BhaProfileList } from "modules/users/BhaProfile/BhaProfileList";
import {
  IdentityFilter,
  PatientSearchFilterValues
} from "modules/users/components/IdentityFilter";
import { ConsumerUserIdentitiesTable } from "modules/users/ConsumerUser/ConsumerUserIdentitiesTable";

const PatientSearchBase: React.FC = () => {
  const theme = useTheme();
  const [filter, setFilter] = useState<PatientSearchFilterValues>();

  const hasSearchCriteria = Boolean(
    filter?.email ||
      filter?.name ||
      filter?.mobile ||
      filter?.profileIdPrefix ||
      filter?.objectId
  );

  const bhcaSearchOn = filter?.bhcaSearch !== false;

  const identityArgs = useMemo<BpIdUsersSearchArgs | undefined>(() => {
    if (!hasSearchCriteria || !filter) return undefined;

    const { bhcaSearch, ...args } = filter;
    return args;
  }, [filter, hasSearchCriteria]);

  const profileSearchText = useMemo(() => {
    if (!bhcaSearchOn) return "";
    return [
      filter?.name,
      filter?.email,
      filter?.mobile,
      filter?.profileIdPrefix
    ]
      .filter(Boolean)
      .join(" ")
      .trim();
  }, [filter, bhcaSearchOn]);

  const {
    data: identityData,
    isFetching: isIdentityFetching
  } = useCUIdentitySearch(identityArgs);

  const {
    data: profileData,
    isFetching: isProfileFetching
  } = useConsumerUserProfileSearch(profileSearchText);

  const renderBhaProfileSection = () => {
    if (!profileSearchText) {
      return (
        <NoDataTile
          textProps={{
            text:
              "Add a name, email, mobile number, or profile ID to search BHAc profiles"
          }}
          linkProps={{ hidden: true }}
          showBoxShadow={false}
        />
      );
    }

    if (isProfileFetching) {
      return <Spinner />;
    }

    if (profileData?.records?.length) {
      return <BhaProfileList profiles={profileData.records} />;
    }

    return (
      <NoDataTile
        textProps={{ text: "No BHAc profiles found" }}
        linkProps={{ hidden: true }}
        showBoxShadow={false}
      />
    );
  };

  return (
    <Stack verticalFill tokens={{ childrenGap: theme.spacing.m }}>
      <Stack tokens={{ childrenGap: theme.spacing.s2 }}>
        <Text variant="xLarge">Patient search</Text>
        <Text variant="medium">
          Find patients who use Best Health in the B2C & BHAc databases
        </Text>
      </Stack>
      <IdentityFilter onFilter={setFilter} />
      {identityData?.tooManyResults === true && (
        <MessageBar messageBarType={MessageBarType.error}>
          Your search returned too many results, please refine your search
          parameters.
        </MessageBar>
      )}
      {!hasSearchCriteria ? (
        <NoDataTile
          textProps={{
            text:
              "Search for patients, turn off BHAc to search identity service (B2C)"
          }}
          linkProps={{ hidden: true }}
          showBoxShadow={false}
        />
      ) : (
        <Stack verticalFill tokens={{ childrenGap: theme.spacing.l1 }}>
          <Stack tokens={{ childrenGap: theme.spacing.s1 }}>
            <Text variant="mediumPlus">B2C identities</Text>
            {identityData?.records?.length || isIdentityFetching ? (
              <ConsumerUserIdentitiesTable
                enableShimmer={isIdentityFetching}
                items={identityData?.records || []}
              />
            ) : (
              <NoDataTile
                textProps={{ text: "No B2C identities found" }}
                linkProps={{ hidden: true }}
                showBoxShadow={false}
              />
            )}
          </Stack>
          {bhcaSearchOn && (
            <Stack tokens={{ childrenGap: theme.spacing.s1 }}>
              <Text variant="mediumPlus">BHAc profiles</Text>
              {renderBhaProfileSection()}
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export const PatientSearch = withPermissions(
  PatientSearchBase,
  [Permissions.PltCatalogUserRead, Permissions.PltCatalogUserWrite],
  "or"
);
